import React from 'react';
import TextField from '@mui/material/TextField';
import AutocompleteMui from '@mui/material/Autocomplete';
import { InputLabel } from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { ControllerFieldState } from 'react-hook-form';

type AutocompleteOption = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: string | number | undefined;
  label: string | undefined;
  group?: string;
};

interface AutocompleteProps {
  options: AutocompleteOption[];
  onChange: (
    value: string | string[] | number | number[] | (string | number)[] | null
  ) => void;
  id?: string | undefined;
  value?: string | string[] | number | number[] | null;
  label?: string;
  color?: string;
  multiple?: boolean;
  required?: boolean;
  isError?: boolean;
  fieldState?: ControllerFieldState;
  freeSolo?: boolean;
  heightClassName?: string;
  sxStyle?: { [key: string]: string | number };
  disabled?: boolean;
}

export const Autocomplete = React.forwardRef<
  typeof AutocompleteMui,
  AutocompleteProps
>(
  (
    {
      id,
      options,
      onChange,
      value,
      label,
      required,
      isError,
      fieldState,
      color = '#1890ff',
      multiple = false,
      freeSolo = false,
      heightClassName = 'h-[30px]',
      sxStyle = { width: 300 },
      disabled = false,
    },
    ref
  ) => {
    const { t } = useTranslation();

    return (
      <div className="relative">
        {label && (
          <InputLabel
            name={id ?? ''}
            text={t(`inputs.labels.${label}`, label.toString() || '') as string}
            required={required}
          />
        )}
        <div className={`${heightClassName}`}>
          <AutocompleteMui
            ref={ref}
            disablePortal
            id={id}
            options={options}
            value={
              !freeSolo
                ? !multiple
                  ? options.find((o) => o.value === value) || null
                  : options.filter((o) => {
                      return (
                        o.value !== undefined &&
                        value instanceof Array &&
                        value?.map((v) => v).includes(o.value)
                      );
                    })
                : value && value instanceof Array && value.map((v) => v)
            }
            onChange={(e, selection) => {
              if (selection === null) {
                onChange(null);
              }

              if (
                selection !== null &&
                !multiple &&
                !(selection instanceof Array) &&
                typeof selection !== 'string' &&
                typeof selection !== 'number' &&
                typeof selection !== 'boolean'
              ) {
                onChange(
                  selection.value !== undefined ? selection.value : null
                );
              } else if (multiple && selection instanceof Array) {
                if (!freeSolo) {
                  const typedSelection = selection as AutocompleteOption[];

                  onChange(typedSelection?.map((s) => s?.value || ''));
                } else {
                  const typedSelection = selection as (string | number)[];

                  onChange(typedSelection);
                }
              }
            }}
            isOptionEqualToValue={(listOption, selectedOption) => {
              if (!freeSolo) {
                const typedSelectedOption =
                  selectedOption as AutocompleteOption;
                const typedListOption = listOption as AutocompleteOption;

                return (
                  typedListOption?.value?.toString() ===
                  typedSelectedOption?.value?.toString()
                );
              } else {
                return false;
              }
            }}
            sx={{
              '&.MuiAutocomplete-root': {
                height: '100%',
                width: '100%',
              },
              '&.MuiAutocomplete-root .MuiFormControl-root': {
                height: '100%',
                width: '100%',
              },
              '&.MuiAutocomplete-root .MuiFormControl-root .MuiInputBase-root':
                {
                  height: '100%',
                  paddingTop: multiple ? '5px' : 0,
                  paddingBottom: multiple ? '5px' : 0,
                },
              '&.MuiAutocomplete-root .MuiOutlinedInput-input:focus': {
                border: 'none',
                boxShadow: 'none',
              },
              '&.MuiAutocomplete-root .MuiInputLabel-root': {
                transition: 'all 0.2s ease-out',
              },
              '&.MuiAutocomplete-root .MuiInputLabel-root:not(.MuiInputLabel-shrink)':
                {
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: '10px',
                  fontSize: '14px',
                },
              '&.MuiAutocomplete-root .MuiInputLabel-root.Mui-focused': {
                color,
              },
              '&.MuiAutocomplete-root .MuiInputLabel-shrink': {
                display: 'none',
              },
              '&.MuiAutocomplete-root input': {
                boxSizing: 'border-box',
                height: '100%',
              },
              '&.MuiAutocomplete-root fieldset': {
                top: 0,
                height: '100%',
              },
              '&.MuiAutocomplete-root legend': {
                display: 'none',
              },
              '&.MuiAutocomplete-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: color,
                },
              ...sxStyle,
            }}
            multiple={multiple}
            freeSolo={freeSolo}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  t(`inputs.labels.${label}`, label?.toString() || '') as string
                }
                sx={{
                  '&.MuiFormControl-root .MuiFormLabel-root': {
                    fontFamily: 'Tajawal !important',
                  },
                  '&.MuiFormControl-root .MuiChip-label': {
                    fontFamily: 'Tajawal !important',
                  },
                }}
              />
            )}
            disabled={disabled}
          />
        </div>
        {isError && (
          <div className="absolute top-[105%] right-0 text-[12px] text-red-500">
            <ReturnTranslatedText
              origintext={fieldState?.error?.message}
              text={`general.${fieldState?.error?.message}`}
            />
          </div>
        )}
      </div>
    );
  }
);
